import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OrgProvider } from './contexts/OrgContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { PortalLayout } from './components/PortalLayout';
import { CommandPalette } from './components/CommandPalette';
import { PageTransition } from './components/PageTransition';
import { ShortcutsDialog } from './components/ShortcutsDialog';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';

// Auth Pages
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import AccountLinkPage from './pages/AccountLinkPage';

// App Pages
import { DashboardPage } from './pages/DashboardPage';
import { ContactsPage } from './pages/ContactsPage';
import ContactDetailPage from './pages/ContactDetailPage';
import VoteSimulationPage from './pages/VoteSimulationPage';
import GedRegistersPage from './pages/GedRegistersPage';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { OpportunitiesPage } from './pages/OpportunitiesPage';
import { DossiersPage } from './pages/DossiersPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { TasksPage } from './pages/TasksPage';
import { PartnersPage } from './pages/PartnersPage';
import { ReportingPage } from './pages/ReportingPage';
import { SettingsPage } from './pages/SettingsPage';
import { UsersPage } from './pages/UsersPage';
import { ActivityLogPage } from './pages/ActivityLogPage';
import { BackupPage } from './pages/BackupPage';

import { PortalOnboardingPage } from './pages/PortalOnboardingPage';
import OnboardingOverviewPage from './pages/OnboardingOverviewPage';
import PaymentResultPage from './pages/PaymentResultPage';
import FinanceDashboardPage from './pages/FinanceDashboardPage';
import SearchPage from './pages/SearchPage';
import TerritoryAnalyticsPage from './pages/TerritoryAnalyticsPage';
import PublicImpactReportPage from './pages/PublicImpactReportPage';
import CalendarPage from './pages/CalendarPage';
import ImpactESSPage from './pages/ImpactESSPage';
import OrgImpactPage from './pages/OrgImpactPage';
import WorkflowsPage from './pages/WorkflowsPage';
import WalletPage from './pages/WalletPage';
import KpiPage from './pages/KpiPage';
import EmailComposePage from './pages/EmailComposePage';
import EmailDeliveryJournalPage from './pages/EmailDeliveryJournalPage';
import PublicationsPage from './pages/PublicationsPage';
import OtoPage from './pages/OtoPage';
import AdhesionPage from './pages/AdhesionPage';
import AssociateRegistryPage from './pages/AssociateRegistryPage';
import RegistryImportPage from './pages/RegistryImportPage';
import SpacesPage from './pages/SpacesPage';
import PublicAdhesionPage from './pages/PublicAdhesionPage';
import JoinCooperativePage from './pages/JoinCooperativePage';
import LandingEditorPage from './pages/LandingEditorPage';
import MissionTemplatesAdminPage from './pages/MissionTemplatesAdminPage';
import TourAdminPage from './pages/TourAdminPage';
import PublicLandingDraftPage from './pages/PublicLandingDraftPage';
import PublicLibraryPage from './pages/PublicLibraryPage';
import LandingVariantsPage from './pages/LandingVariantsPage';
import PortalResourcesAdminPage from './pages/PortalResourcesAdminPage';
import MissionsAdminPage from './pages/MissionsAdminPage';
import NotificationsTimelinePage from './pages/NotificationsTimelinePage';
import PaymentSenderPage from './pages/PaymentSenderPage';
import EmailSegmentsPage from './pages/EmailSegmentsPage';
import InvoicesPage from './pages/InvoicesPage';
import TvaSettingsPage from './pages/TvaSettingsPage';
import PublicQuotePage from './pages/PublicQuotePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// CMC Pages
import CmcDashboardPage from './pages/cmc/CmcDashboardPage';
import CmcCooperatorsPage from './pages/cmc/CmcCooperatorsPage';
import CmcUccPage from './pages/cmc/CmcUccPage';
import CmcRulesPage from './pages/cmc/CmcRulesPage';
import CmcContractsPage from './pages/cmc/CmcContractsPage';
import CmcCipascopPage from './pages/cmc/CmcCipascopPage';
import CmcApportIndustriePage from './pages/cmc/CmcApportIndustriePage';
import CmcPaymentsPage from './pages/cmc/CmcPaymentsPage';
import CmcAttestationsPage from './pages/cmc/CmcAttestationsPage';
import CmcAuditPage from './pages/cmc/CmcAuditPage';
import CmcVerifyPublicPage from './pages/cmc/CmcVerifyPublicPage';

// Portal Pages
import { PortalLoginPage } from './pages/PortalLoginPage';
import { PortalDashboardPage } from './pages/PortalDashboardPage';
import { PortalDossiersPage } from './pages/PortalDossiersPage';
import { PortalDocumentsPage } from './pages/PortalDocumentsPage';
import { PortalPaymentsPage } from './pages/PortalPaymentsPage';
import { PortalConventionsPage } from './pages/PortalConventionsPage';
import { PortalMissionsPage } from './pages/PortalMissionsPage';
import { PortalPublicationsPage } from './pages/PortalPublicationsPage';
import { PortalDirectoryPage } from './pages/PortalDirectoryPage';

// Client Portal Pages
import ClientLoginPage from './pages/ClientLoginPage';
import ClientLayout from './components/ClientLayout';
import ClientDashboardPage from './pages/ClientDashboardPage';
import ClientSchedulesPage from './pages/ClientSchedulesPage';
import ClientPaymentsPage from './pages/ClientPaymentsPage';
import ClientDocumentsPage from './pages/ClientDocumentsPage';
import ClientDossiersPage from './pages/ClientDossiersPage';
import OrgSelectionPage from './pages/OrgSelectionPage';

function GlobalShortcuts() {
  useKeyboardShortcuts();
  return null;
}

function HomeRoute() {
  // La racine affiche toujours la page publique d'adhésion, même connecté.
  return <JoinCooperativePage />;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
    <OrgProvider>
      <BrowserRouter>
        <GlobalShortcuts />
        <CommandPalette />
        <ShortcutsDialog />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<AccountLinkPage key="forgot" mode="forgot" />} />
          <Route path="/reset-password" element={<AccountLinkPage key="reset" mode="reset" />} />
          <Route path="/accept-invitation" element={<AccountLinkPage key="invitation" mode="invitation" />} />
          <Route path="/select-org" element={
            <ProtectedRoute>
              <OrgSelectionPage />
            </ProtectedRoute>
          } />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout><PageTransition><DashboardPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/contacts" element={
            <ProtectedRoute>
              <Layout><PageTransition><ContactsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/contacts/new" element={
            <ProtectedRoute>
              <Layout><PageTransition><ContactsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/contacts/:id" element={
            <ProtectedRoute>
              <Layout><PageTransition><ContactDetailPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/organizations" element={
            <ProtectedRoute>
              <Layout><PageTransition><OrganizationsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/organizations/new" element={
            <ProtectedRoute>
              <Layout><PageTransition><OrganizationsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/organizations/:id" element={
            <ProtectedRoute>
              <Layout><PageTransition><OrganizationsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/opportunities" element={
            <ProtectedRoute>
              <Layout><PageTransition><OpportunitiesPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/opportunities/new" element={
            <ProtectedRoute>
              <Layout><PageTransition><OpportunitiesPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/dossiers" element={
            <ProtectedRoute>
              <Layout><PageTransition><DossiersPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/dossiers/new" element={
            <ProtectedRoute>
              <Layout><PageTransition><DossiersPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/dossiers/:id" element={
            <ProtectedRoute>
              <Layout><PageTransition><DossiersPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute>
              <Layout><PageTransition><DocumentsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/payments" element={
            <ProtectedRoute>
              <Layout><PageTransition><PaymentsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/tasks" element={
            <ProtectedRoute>
              <Layout><PageTransition><TasksPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/tasks/new" element={
            <ProtectedRoute>
              <Layout><PageTransition><TasksPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/partners" element={
            <ProtectedRoute>
              <Layout><PageTransition><PartnersPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/reporting" element={
            <ProtectedRoute>
              <Layout><PageTransition><ReportingPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/vote-simulation" element={
            <ProtectedRoute>
              <Layout><PageTransition><VoteSimulationPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/ged/registers" element={
            <ProtectedRoute>
              <Layout><PageTransition><GedRegistersPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Layout><PageTransition><SettingsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute>
              <Layout><PageTransition><UsersPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/activity" element={
            <ProtectedRoute>
              <Layout><PageTransition><ActivityLogPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/backup" element={
            <ProtectedRoute>
              <Layout><PageTransition><BackupPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Layout><PageTransition><OnboardingOverviewPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/paiement/succes" element={
            <ProtectedRoute>
              <Layout><PageTransition><PaymentResultPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/paiement/annule" element={
            <ProtectedRoute>
              <Layout><PageTransition><PaymentResultPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/finance" element={
            <ProtectedRoute>
              <Layout><PageTransition><FinanceDashboardPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/search" element={
            <ProtectedRoute>
              <Layout><PageTransition><SearchPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/territoires" element={
            <ProtectedRoute>
              <Layout><PageTransition><TerritoryAnalyticsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/calendrier" element={
            <ProtectedRoute>
              <Layout><PageTransition><CalendarPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/impact-ess" element={
            <ProtectedRoute>
              <Layout><PageTransition><ImpactESSPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/impact-org" element={
            <ProtectedRoute>
              <Layout><PageTransition><OrgImpactPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/workflows" element={
            <ProtectedRoute>
              <Layout><PageTransition><WorkflowsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/wallet" element={
            <ProtectedRoute>
              <Layout><PageTransition><WalletPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/kpi" element={
            <ProtectedRoute>
              <Layout><PageTransition><KpiPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/emails/composer" element={
            <ProtectedRoute>
              <Layout><PageTransition><EmailComposePage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/emails/journal" element={<ProtectedRoute><Layout><PageTransition><EmailDeliveryJournalPage /></PageTransition></Layout></ProtectedRoute>} />
          <Route path="/oto" element={<ProtectedRoute><Layout><PageTransition><OtoPage /></PageTransition></Layout></ProtectedRoute>} />
          <Route path="/publications" element={
            <ProtectedRoute>
              <Layout><PageTransition><PublicationsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/adhesion" element={
            <ProtectedRoute>
              <Layout><PageTransition><AdhesionPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/adhesion/registre" element={<ProtectedRoute><Layout><PageTransition><AssociateRegistryPage /></PageTransition></Layout></ProtectedRoute>} />
          <Route path="/adhesion/registre/import" element={<ProtectedRoute><Layout><PageTransition><RegistryImportPage /></PageTransition></Layout></ProtectedRoute>} />
          <Route path="/espaces" element={
            <ProtectedRoute>
              <Layout><PageTransition><SpacesPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/adhesion/:token" element={<PublicAdhesionPage />} />
          <Route path="/rejoindre" element={<JoinCooperativePage />} />
          <Route path="/confidentialite" element={<PrivacyPolicyPage />} />
          <Route path="/verifier-email" element={<VerifyEmailPage />} />
          <Route path="/admin/landing" element={
            <ProtectedRoute>
              <Layout><LandingEditorPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/templates" element={
            <ProtectedRoute>
              <Layout><MissionTemplatesAdminPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/tour" element={
            <ProtectedRoute>
              <Layout><TourAdminPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/rejoindre/draft/:token" element={<PublicLandingDraftPage />} />
          <Route path="/portal/library" element={<PublicLibraryPage />} />
          <Route path="/admin/landing-ab" element={
            <ProtectedRoute>
              <Layout><LandingVariantsPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/portal-resources" element={
            <ProtectedRoute>
              <Layout><PortalResourcesAdminPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/missions" element={
            <ProtectedRoute>
              <Layout><MissionsAdminPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/emails/notifications" element={
            <ProtectedRoute>
              <Layout><PageTransition><NotificationsTimelinePage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/emails/segments" element={
            <ProtectedRoute>
              <Layout><PageTransition><EmailSegmentsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/paiements/envoi" element={
            <ProtectedRoute>
              <Layout><PageTransition><PaymentSenderPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/factures" element={
            <ProtectedRoute>
              <Layout><PageTransition><InvoicesPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/factures/tva" element={
            <ProtectedRoute>
              <Layout><PageTransition><TvaSettingsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />

          {/* CMC O'SCOP routes */}
          <Route path="/cmc" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcDashboardPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/cooperators" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcCooperatorsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/ucc" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcUccPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/rules" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcRulesPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/contracts" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcContractsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/cipascop" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcCipascopPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/apport-industrie" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcApportIndustriePage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/payments" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcPaymentsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/attestations" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcAttestationsPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />
          <Route path="/cmc/audit" element={
            <ProtectedRoute>
              <Layout><PageTransition><CmcAuditPage /></PageTransition></Layout>
            </ProtectedRoute>
          } />

          {/* Public CMC verification (no auth) */}
          <Route path="/verify/:token" element={<CmcVerifyPublicPage />} />
          <Route path="/cmc/verify/:token" element={<CmcVerifyPublicPage />} />
          <Route path="/portal/quotes/:token" element={<PublicQuotePage />} />
          
          {/* Portal routes */}
          <Route path="/portail" element={<PortalLoginPage />} />
          <Route path="/portail/dashboard" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="dashboard"><PortalDashboardPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portail/onboarding" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="onboarding"><PortalOnboardingPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portail/dossiers" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="dossiers"><PortalDossiersPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portail/documents" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="documents"><PortalDocumentsPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portail/conventions" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="conventions"><PortalConventionsPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portail/missions" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="missions"><PortalMissionsPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portail/publications" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="publications"><PortalPublicationsPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portail/annuaire" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="annuaire"><PortalDirectoryPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portail/paiements" element={
            <ProtectedRoute portalRoute>
              <PortalLayout activePage="paiements"><PortalPaymentsPage /></PortalLayout>
            </ProtectedRoute>
          } />
          
          {/* Client Portal routes */}
          <Route path="/espace-client/login" element={<ClientLoginPage />} />
          <Route path="/rapport/:token" element={<PublicImpactReportPage />} />
          <Route path="/espace-client" element={<ClientLayout activePage="dashboard"><ClientDashboardPage /></ClientLayout>} />
          <Route path="/espace-client/dossiers" element={<ClientLayout activePage="dossiers"><ClientDossiersPage /></ClientLayout>} />
          <Route path="/espace-client/echeanciers" element={<ClientLayout activePage="echeanciers"><ClientSchedulesPage /></ClientLayout>} />
          <Route path="/espace-client/paiements" element={<ClientLayout activePage="paiements"><ClientPaymentsPage /></ClientLayout>} />
          <Route path="/espace-client/documents" element={<ClientLayout activePage="documents"><ClientDocumentsPage /></ClientLayout>} />
          <Route path="/espace-client/paiement-succes" element={<ClientLayout activePage="paiements"><ClientPaymentsPage /></ClientLayout>} />
          <Route path="/espace-client/paiement-annule" element={<ClientLayout activePage="paiements"><ClientPaymentsPage /></ClientLayout>} />

          {/* Default redirect */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </OrgProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
